import type { PaymentModelSupplier, ReceiptModel } from '~/utils/receipt-model/ReceiptModel'
import { conventionalNameFormat } from '~/utils/formatter/name'

/** Distributor selected in the form (`hardware_supplier`). `value <= 0` = none. */
export interface SupplierValue {
  name: string
  value: number
}

/** Distributor selector option: grouped into partner × other authorized distributors. */
export interface SupplierOption extends SupplierValue {
  isPartner: boolean
}

type SavedSupplier = ReceiptModel['supplier']

/** The GET returns the raw name (upper case etc.): same format as the catalog (`useSuppliers`). */
export function formatSupplierName(name: string | null | undefined): string {
  const trimmed = name?.trim() ?? ''
  return trimmed ? conventionalNameFormat(trimmed) : ''
}

/** Value of the form field `hardware_supplier` (partial while nothing was chosen). */
export type SupplierFieldValue = Partial<SupplierValue> | null | undefined

/** Name of the distributor chosen in the form; empty when there is none (`value <= 0`). */
export function getSelectedSupplierName(option: SupplierFieldValue): string {
  const value = option?.value
  return value && value > 0 ? (option?.name?.trim() ?? '') : ''
}

/** Id of the distributor chosen in the form, or `null`. */
export function getSelectedSupplierId(option: SupplierFieldValue): number | null {
  const value = option?.value
  return typeof value === 'number' && value > 0 ? value : null
}

/**
 * Partner? Catalog first, then the distributor saved in the GET. No distributor or no information: `true`
 * (the billing letter is the default path).
 */
export function resolveSupplierIsPartner(
  supplierId: number | null | undefined,
  catalog: PaymentModelSupplier[] | null | undefined,
  saved: SavedSupplier | null | undefined,
): boolean {
  if (!supplierId)
    return true

  const fromCatalog = catalog?.find(supplier => supplier.id === supplierId)
  if (fromCatalog)
    return fromCatalog.is_partner !== false

  if (saved?.id === supplierId)
    return saved.is_partner !== false

  return true
}

/** Selector options: catalog + the saved distributor, when it is no longer in the catalog. */
export function buildSupplierOptions(
  catalog: PaymentModelSupplier[] | null | undefined,
  saved: SavedSupplier | null | undefined,
): SupplierOption[] {
  const options: SupplierOption[] = (catalog ?? []).map(supplier => ({
    name: supplier.company_name,
    value: supplier.id,
    isPartner: supplier.is_partner !== false,
  }))

  const savedId = saved?.id
  if (savedId != null && savedId > 0 && !options.some(option => option.value === savedId)) {
    options.push({
      name: formatSupplierName(saved?.name),
      value: savedId,
      isPartner: saved?.is_partner !== false,
    })
  }

  return options
}
